import * as React from 'react'
import cx from 'classnames'
import { toastError } from '@src/utils/toast'
import EditorGuardrail from '@src/modules/guardrails/components/editor.guardrail'
import * as styles from './conditions.styl'
import Button from '@src/components/common/button'
import {
  GuardrailEditModel,
  SeverityEnum
} from '@src/modules/guardrails/models'
import { trim } from 'lodash'
import * as Selectors from '../../selectors'
import { useDispatch, useSelector } from 'react-redux'
import {
  autocompleteGetOptions,
  autocompleteSessionSearch,
  autocompleteUserSearch
} from '@src/redux/actions/autocomplete.actions'
import {
  createMonitor,
  CreateMonitorOptions
} from '@src/redux/actions/guardrails/monitoring.actions'
import { getHtmlSelectorValueBy } from '@src/utils/css.selector.helper'
import { getDataToServer } from '@src/modules/guardrails/v1/helpers'
import { useFeatureFlagsContext } from '@src/modules/feature.flags/context'

interface OwnProps {
  searchQuery: string
}

export type Props = OwnProps

interface Condition {
  selectedOptionIndex: number
  conditionItem: string
  andItem: string
}

interface ConditionState {
  conditions: Condition[]
  selectedOptionIndex: number
  openedConditionOptionIndex: number
}
const defaultState: ConditionState = {
  conditions: [
    {
      selectedOptionIndex: 0,
      conditionItem: '',
      andItem: ' '
    }
  ],
  selectedOptionIndex: 0,
  openedConditionOptionIndex: -1
}

const TileQueryConditions: React.FC<Props> = React.memo(
  (props: React.PropsWithChildren<Props>) => {
    const { children, searchQuery } = props
    const isUnMount = React.useRef(false)
    const dispatch = useDispatch()
    const [openedEditor, openEditor] = React.useState(false)
    const [state, setState] = React.useState<ConditionState>(defaultState)
    const [isCollapsed, collapse] = React.useState<boolean>(true)
    const searchData = useSelector(Selectors.getSearchData)
    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const { CAM_TEMP_20210817_ALERTS_IMPROVEMENTS } = useFeatureFlagsContext()

    const useAlertV2 = CAM_TEMP_20210817_ALERTS_IMPROVEMENTS?.response?.value

    React.useEffect(() => {
      if (!searchQuery) {
        dispatch(autocompleteUserSearch({ input: '' }))
      }
      document.addEventListener('mouseout', handleClickOutside)
      return () => {
        isUnMount.current = true
        document.removeEventListener('mouseout', handleClickOutside)
      }
    }, [])

    function handleClickOutside(event) {
      if (
        wrapperRef?.current &&
        !wrapperRef?.current?.contains(event.target) &&
        !isUnMount
      ) {
        setState(prev => ({ ...prev, openedConditionOptionIndex: -1 }))
      }
    }

    function handleAdd() {
      const selectedItem =
        searchData.length > 0 ? searchData[state?.selectedOptionIndex] : ''
      const query = (searchQuery || '').replace('\n', '') + selectedItem + ' '
      dispatch(autocompleteGetOptions(query))
      setState(prev => ({ ...prev, selectedOptionIndex: 0 }))
    }

    function handleRemove() {
      if (!searchQuery) return
      const lastSpaceIndex = searchQuery
        .replace('\n', '')
        .lastIndexOf(' ', searchQuery.length - 2)
      let query = searchQuery.replace('\n', '').slice(0, lastSpaceIndex) + ' '
      if (lastSpaceIndex <= -1) {
        query = ''
      }
      dispatch(autocompleteGetOptions(query))
      setState(prev => ({ ...prev, selectedOptionIndex: 0 }))
    }

    function handleExecute() {
      const query = trim(searchQuery || '') + '\n'
      setState(prev => ({ ...prev, selectedOptionIndex: 0 }))
      dispatch(
        autocompleteSessionSearch({
          input: query,
          showToastMessages: true
        })
      )
    }

    function onCloseModal() {
      openEditor(false)
    }

    function openCreateMonitorDialog() {
      openEditor(true)
    }

    function onCreateMonitor(model: GuardrailEditModel, categories?: string[]) {
      if (model?.name.trim().length === 0) {
        toastError('Guardrail name should not be empty')
      } else {
        const reqParams: CreateMonitorOptions = {
          ...getDataToServer(model),
          query: searchQuery
        }

        dispatch(
          createMonitor(reqParams, useAlertV2, () => {
            onCloseModal()
          })
        )
      }
    }

    const onClickByCondition = (index: number) => () => {
      setState(prev => ({
        ...prev,
        openedConditionOptionIndex: index
      }))
    }

    function handleCollapse() {
      collapse(prevState => !prevState)
    }

    function selectOption(conditionIndex: number, optionIndex: number) {
      setState(prev => ({
        ...prev,
        selectedOptionIndex: optionIndex,
        openedConditionOptionIndex: -1
      }))
    }

    const onSelectOption =
      (conditionIndex: number, index: number) =>
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation()
        selectOption(conditionIndex, index)
      }

    function renderSelectableOptions(
      options: string[] = [],
      conditionIndex: number
    ) {
      return (
        <div className={styles.options}>
          {options.map((item, index) => (
            <div
              role={`${getHtmlSelectorValueBy(
                index.toString(),
                `selectable_option_`,
                '_',
                'lower'
              )}`}
              key={index}
              className={cx(styles.option, {
                [styles.selected]: state?.selectedOptionIndex === index
              })}
              onClick={onSelectOption(conditionIndex, index)}
            >
              <div className={styles.checkmark}>
                <i className="material-icons">check</i>
              </div>
              <div className={styles.optionText}>{item}</div>
            </div>
          ))}
        </div>
      )
    }

    return (
      <div className={styles.tile}>
        <div className={styles.header}>
          <i className="material-icons">notifications</i>
          <div className={styles.title}>Query Conditions</div>
          <Button
            icon={'notifications'}
            className={styles.createRuleButton}
            onClick={openCreateMonitorDialog}
            title={'CREATE GUARDRAIL RULE'}
          />

          <EditorGuardrail
            contentText={
              'Creating a GUARDRAIL QUERY will set up an alert system when there is an access change to the queried object.'
            }
            open={openedEditor}
            titleSave={`CREATE GUARDRAIL QUERY`}
            onClose={onCloseModal}
            onPost={onCreateMonitor}
            showQueryField
            data={{
              name: '',
              severity: SeverityEnum.HIGH,
              query: searchQuery
            }}
          />
        </div>
        <div className={styles.query}>
          <div className={styles.textLeft}>Query:</div>
          <div className={styles.searchQuery}>{searchQuery}</div>
          <i
            role={'toggle-collapse'}
            className="material-icons"
            onClick={handleCollapse}
          >
            keyboard_arrow_down
          </i>
        </div>
        <div
          className={cx(styles.conditionSection, {
            [styles.collapsed]: isCollapsed
          })}
        >
          <div className={styles.textLeft}>IF:</div>
          <div className={styles.conditions}>
            {(state?.conditions || []).map((item, index) => (
              <div
                role={`condition_wrapper`}
                key={index}
                className={styles.condition}
                ref={wrapperRef}
              >
                <div className={styles.searchQuery}>{searchQuery}</div>
                <div
                  role={'open_condition'}
                  className={styles.selectedOption}
                  onClick={onClickByCondition(index)}
                >
                  {searchData.length <= 0 ? null : (
                    <div className={styles.optionDropdown}>
                      <div className={styles.selectedOptionText}>
                        {searchData[state.selectedOptionIndex]}
                      </div>
                      <div className={styles.arrowDropDown} />
                      <div className={styles.optionsBox}>
                        {state.openedConditionOptionIndex === index
                          ? renderSelectableOptions(searchData, index)
                          : null}
                      </div>
                    </div>
                  )}
                </div>
                <Button icon={'add'} title={'ADD'} onClick={handleAdd} />
                <Button
                  icon={'remove'}
                  title={'REMOVE'}
                  onClick={handleRemove}
                />
                <Button title={'EXECUTE'} onClick={handleExecute} />
              </div>
            ))}
          </div>
        </div>
        <div
          className={cx(styles.effectedObjects, {
            [styles.collapsed]: isCollapsed
          })}
        >
          {children}
        </div>
      </div>
    )
  }
)

export default TileQueryConditions
